/**
 * FOOTER — Rediseño B2B SaaS Profesional
 * 
 * - Fondo oscuro sólido (no gradiente)
 * - Grid 3 columnas (no 4)
 * - Links con hover underline
 * - Icons consistentes
 */

import React from 'react';
import { 
  Mail, 
  Phone,
  MapPin,
  Shield,
  CheckCircle2
} from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navLinks = [
    { label: 'El Problema', id: 'problema' },
    { label: 'La Solución', id: 'solucion' },
    { label: 'Cómo Funciona', id: 'como-funciona' },
    { label: 'FAQ', id: 'faq' },
    { label: 'Contacto', id: 'formulario-contacto' }
  ];

  return (
    <footer className="w-full bg-[#0b1220] text-white">
      <div className="max-w-[1120px] mx-auto px-6">
        
        {/* Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 py-12 border-b border-white/10">
          
          {/* Columna 1 - About */}
          <div className="flex flex-col gap-4">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="16" height="16" fill="white"/>
                <rect y="24" width="24" height="8" fill="white"/>
                <rect y="16" width="8" height="8" fill="white"/>
                <rect x="16" width="16" height="8" fill="white"/>
                <rect x="24" y="8" width="8" height="16" fill="white"/>
                <path d="M32 24L24 32V24H32Z" fill="white"/>
              </svg>
              <div className="flex flex-col leading-tight">
                <span className="text-[16px] font-bold text-white">
                  Dashwise
                </span>
                <span className="text-[11px] font-medium text-white/60">
                  Transporte Perú
                </span>
              </div>
            </div>

            <p className="text-[14px] text-white/70 leading-[1.6] max-w-[280px]">
              Software especializado para empresas de transporte de carga en Perú. Control total de rentabilidad por viaje, vehículo y cliente.
            </p>

            <div className="flex items-center gap-2 text-[13px] text-white/60">
              <MapPin size={14} strokeWidth={2} />
              <span>Lima, Perú</span>
            </div>
          </div>

          {/* Columna 2 - Links */}
          <div className="flex flex-col gap-4">
            <h4 className="text-[15px] font-bold text-white">
              Navegación
            </h4>
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="text-[14px] text-white/70 hover:text-white text-left transition-colors relative group w-fit"
                >
                  {link.label}
                  <span className="absolute bottom-0 left-0 right-0 h-px bg-white scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
                </button>
              ))}
            </nav>
          </div>

          {/* Columna 3 - Contact */}
          <div className="flex flex-col gap-4">
            <h4 className="text-[15px] font-bold text-white">
              Contacto
            </h4>
            
            <div className="flex flex-col gap-3">
              <a
                href="tel:+51991928825"
                className="flex items-center gap-2 text-[14px] text-white/70 hover:text-white transition-colors group"
              >
                <Phone size={14} strokeWidth={2} />
                <span className="relative">
                  +51 991 928 825
                  <span className="absolute bottom-0 left-0 right-0 h-px bg-white scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
                </span>
              </a>

              <a
                href="mailto:ecorrea@dashwisetech.com"
                className="flex items-center gap-2 text-[14px] text-white/70 hover:text-white transition-colors group"
              >
                <Mail size={14} strokeWidth={2} />
                <span className="relative">
                  ecorrea@dashwisetech.com
                  <span className="absolute bottom-0 left-0 right-0 h-px bg-white scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
                </span>
              </a>
            </div>

            {/* Badges */}
            <div className="flex flex-col gap-2 mt-2">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md bg-white/10 flex items-center justify-center">
                  <CheckCircle2 size={12} strokeWidth={2} className="text-emerald-400" />
                </div>
                <span className="text-[12.5px] text-white/60">
                  100% Seguro
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md bg-white/10 flex items-center justify-center">
                  <CheckCircle2 size={12} strokeWidth={2} className="text-emerald-400" />
                </div>
                <span className="text-[12.5px] text-white/60">
                  Sin permanencia
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 py-6">
          <p className="text-[13px] text-white/50">
            © {currentYear} Dashwise. Todos los derechos reservados.
          </p>
          <div className="flex items-center gap-4">
            <a
              href="#privacidad"
              className="text-[13px] text-white/50 hover:text-white transition-colors relative group"
            >
              Política de Privacidad
              <span className="absolute bottom-0 left-0 right-0 h-px bg-white scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
            </a>
            <span className="text-white/30">•</span>
            <a
              href="#terminos"
              className="text-[13px] text-white/50 hover:text-white transition-colors relative group"
            >
              Términos de Servicio
              <span className="absolute bottom-0 left-0 right-0 h-px bg-white scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
            </a>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;